"use client";

import { useEffect, useRef, useState } from 'react';
import { Search, Loader2, Map, User, MapPin } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { Input } from '@/components/ui/input';
import { useSearch } from '@/lib/contexts/SearchContext';
import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';

export function GlobalSearch() {
  const router = useRouter();
  const { searchTerm, setSearchTerm, results, isSearching, performSearch } = useSearch();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebounce(searchTerm, 300);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (open) {
      performSearch(debouncedSearch);
    }
  }, [debouncedSearch, open, performSearch]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'map':
        return <Map className="h-4 w-4" />;
      case 'customer':
        return <User className="h-4 w-4" />;
      case 'marker':
        return <MapPin className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="relative w-full h-6 sm:h-7 md:h-9 justify-start text-[10px] sm:text-xs md:text-sm text-muted-foreground px-1.5 sm:px-2 md:px-3 min-w-0"
        onClick={() => setOpen(true)}
      >
        <Search className="h-3 w-3 sm:h-3.5 md:h-4" />
        <span className="ml-1 sm:ml-1.5 md:ml-2 truncate">Search...</span>
        <kbd className="pointer-events-none absolute right-1 sm:right-1.5 top-1 sm:top-1.5 hidden h-4 sm:h-5 md:h-6 select-none items-center gap-0.5 sm:gap-1 rounded border bg-muted px-1 sm:px-1.5 font-mono text-[8px] sm:text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-[8px] sm:text-[10px]">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen} className="max-w-[92vw] sm:max-w-[95vw] md:max-w-lg">
        <DialogHeader className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3">
          <DialogTitle className="text-xs sm:text-sm md:text-base">Search</DialogTitle>
        </DialogHeader>
        <CommandInput
          ref={inputRef}
          placeholder="Search maps, customers, markers..."
          value={searchTerm}
          onValueChange={setSearchTerm}
          className="h-7 sm:h-8 md:h-10 text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4"
        />
        <CommandList className="bg-black/30 dark:bg-gray-900/50 backdrop-blur-md">
          <CommandEmpty className="py-1.5 sm:py-2 md:py-3 text-[10px] sm:text-xs md:text-sm">
            {isSearching ? (
              <div className="flex items-center justify-center gap-1 sm:gap-1.5 md:gap-2">
                <Loader2 className="h-3 w-3 sm:h-3.5 md:h-4 animate-spin" />
                <span>Searching...</span>
              </div>
            ) : (
              'No results found.'
            )}
          </CommandEmpty>
          
          {results.length > 0 && (
            <CommandGroup 
              heading={searchTerm ? "Results" : "Recent"}
              className="text-white/90 dark:text-white/90 text-[10px] sm:text-xs md:text-sm"
            >
              {results.map((result) => (
                <CommandItem
                  key={`${result.type}-${result.id}`}
                  onSelect={() => {
                    router.push(result.link);
                    setOpen(false);
                  }}
                  className="hover:bg-white/10 dark:hover:bg-gray-700/30 text-white/90 dark:text-white/90 py-1 sm:py-1.5 md:py-2 px-1.5 sm:px-2 md:px-3"
                >
                  <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 min-w-0">
                    <div className="flex-shrink-0">
                      {getIcon(result.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{result.title}</div>
                      <div className="text-[8px] sm:text-[10px] md:text-xs text-muted-foreground truncate">
                        {result.subtitle}
                      </div>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
} 